/**
 * Buntu Companion admin module.
 *
 * The same tabs serve both admin surfaces: a Tenant Admin works inside their
 * own tenant, while a Platform Super Admin picks a tenant and additionally gets
 * platform defaults plus per-tenant allow/lock controls.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Laptop,
  LifeBuoy,
  Lock,
  MonitorSmartphone,
  Package,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { apiService } from '../services/api';
import PageLayout from './PageLayout';
import { HubPanel, HubTabs } from './ui/HubShell';
import CompanionSettingsForm, { CompanionSettingsPayload } from './companion/CompanionSettingsForm';
import CompanionUserApp from './companion/CompanionUserApp';
import BuntuCompanionSettings from './BuntuCompanionSettings';

type Surface = 'desktop' | 'portal';

type Tab =
  | 'overview'
  | 'installations'
  | 'adoption'
  | 'tickets'
  | 'notifications'
  | 'consents'
  | 'versions'
  | 'ai'
  | 'settings'
  | 'preview';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'installations', label: 'Installations' },
  { id: 'adoption', label: 'Adoption' },
  { id: 'tickets', label: 'Support Tickets' },
  { id: 'notifications', label: 'Portal notifications' },
  { id: 'consents', label: 'Remote Consent Logs' },
  { id: 'versions', label: 'Companion Versions' },
  { id: 'ai', label: 'AI Assistant Settings' },
  { id: 'settings', label: 'Portal settings' },
  { id: 'preview', label: 'User Preview' },
];

interface Props {
  /** 'platform' adds the tenant selector, platform defaults and lock controls. */
  mode?: 'tenant' | 'platform';
  /** Jump to another Hub tab (e.g. App Deployment) when IT acts on a ticket. */
  onNavigate?: (tabId: string) => void;
}

interface PlatformTenantRow {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  configured: boolean;
  platform_allowed: boolean;
  locked_sections: string[];
  enabled: boolean;
  supported_os: string[];
  installations: number;
  tickets_open: number;
  consent_pending: number;
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const StatCard: React.FC<{ label: string; value: React.ReactNode; hint?: string; icon?: React.ReactNode }> = ({
  label,
  value,
  hint,
  icon,
}) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      {icon}
    </div>
    <p className="mt-2 text-2xl font-semibold text-slate-800">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Badge: React.FC<{ tone: 'green' | 'amber' | 'red' | 'slate' | 'blue'; children: React.ReactNode }> = ({
  tone,
  children,
}) => {
  const tones: Record<string, string> = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const BuntuCompanionHub: React.FC<Props> = ({ mode = 'tenant', onNavigate }) => {
  const isPlatform = mode === 'platform';
  const [surface, setSurface] = useState<Surface>(isPlatform ? 'portal' : 'desktop');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [resolutionDrafts, setResolutionDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tenants, setTenants] = useState<PlatformTenantRow[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');

  const [overview, setOverview] = useState<any>(null);
  const [adoption, setAdoption] = useState<any[]>([]);
  const [installations, setInstallations] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [consents, setConsents] = useState<any[]>([]);
  const [versions, setVersions] = useState<any>(null);
  const [settings, setSettings] = useState<CompanionSettingsPayload | null>(null);
  const [platformDefaults, setPlatformDefaults] = useState<CompanionSettingsPayload | null>(null);

  const [notifDraft, setNotifDraft] = useState({ title: '', message: '', severity: '', category: '' });

  // The preview registers a real companion session, so it needs the Hub token once.
  const hubToken = useMemo(() => {
    try {
      return localStorage.getItem('uem_token');
    } catch {
      return null;
    }
  }, []);

  const tenantRow = useMemo(
    () => tenants.find((t) => t.tenant_id === selectedTenant) || null,
    [tenants, selectedTenant]
  );

  const loadTenants = useCallback(async () => {
    if (!isPlatform) return;
    try {
      const res = await apiService.listCompanionPlatformTenants();
      const items: PlatformTenantRow[] = res?.data?.items || [];
      setTenants(items);
      setSelectedTenant((prev) => prev || items[0]?.tenant_id || '');
    } catch (e: any) {
      setError(e?.message || 'Could not load tenants');
    }
  }, [isPlatform]);

  // Platform reads are scoped per request; tenant admins are pinned server-side.
  const scope = isPlatform ? selectedTenant || undefined : undefined;

  const loadSettings = useCallback(async () => {
    if (isPlatform) {
      if (!selectedTenant) return;
      const res = await apiService.getCompanionTenantSettings(selectedTenant);
      setSettings(res?.data || null);
      const defaults = await apiService.getCompanionPlatformDefaults();
      setPlatformDefaults(defaults?.data || null);
    } else {
      const res = await apiService.getCompanionSettings();
      setSettings(res?.data || null);
    }
  }, [isPlatform, selectedTenant]);

  const loadAll = useCallback(async () => {
    if (isPlatform && !selectedTenant) return;
    setLoading(true);
    setError(null);
    try {
      const [ov, ad, inst, tk, nt, cs, vs] = await Promise.all([
        apiService.getCompanionOverview(scope),
        apiService.getCompanionAdoption(scope),
        apiService.listCompanionInstallations(undefined, scope),
        apiService.listCompanionTickets(undefined, scope),
        apiService.listCompanionNotifications(scope),
        apiService.listCompanionConsents(undefined, scope),
        apiService.getCompanionVersions(scope),
      ]);
      setOverview(ov?.data || null);
      setAdoption(ad?.data?.items || []);
      setInstallations(inst?.data?.items || []);
      setTickets(tk?.data?.items || []);
      setNotifications(nt?.data?.items || []);
      setConsents(cs?.data?.items || []);
      setVersions(vs?.data || null);
      await loadSettings();
    } catch (e: any) {
      setError(e?.message || 'Could not load companion data');
    } finally {
      setLoading(false);
    }
  }, [isPlatform, selectedTenant, scope, loadSettings]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveSettings = async (patch: Record<string, any>) => {
    setSaving(true);
    try {
      if (isPlatform) {
        await apiService.updateCompanionTenantSettings(selectedTenant, patch);
      } else {
        await apiService.updateCompanionSettings(patch);
      }
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const savePlatformDefaults = async (patch: Record<string, any>) => {
    setSaving(true);
    try {
      await apiService.updateCompanionPlatformDefaults(patch);
      const defaults = await apiService.getCompanionPlatformDefaults();
      setPlatformDefaults(defaults?.data || null);
    } finally {
      setSaving(false);
    }
  };

  const toggleTenantAllowed = async (allowed: boolean) => {
    if (!selectedTenant) return;
    try {
      await apiService.updateCompanionTenantControls(selectedTenant, { platform_allowed: allowed });
      setNotice(allowed ? 'Companion allowed for this tenant.' : 'Companion withheld from this tenant.');
      await Promise.all([loadTenants(), loadSettings()]);
    } catch (e: any) {
      setError(e?.message || 'Could not update platform controls');
    }
  };

  const toggleSectionLock = async (section: string) => {
    if (!settings) return;
    const current = new Set(settings.locked_sections || []);
    if (current.has(section)) current.delete(section);
    else current.add(section);
    try {
      await apiService.updateCompanionTenantControls(selectedTenant, {
        locked_sections: Array.from(current),
      });
      await Promise.all([loadTenants(), loadSettings()]);
    } catch (e: any) {
      setError(e?.message || 'Could not update locked sections');
    }
  };

  const updateInstallation = async (id: string, status: string) => {
    try {
      await apiService.updateCompanionInstallation(id, status, scope);
      setNotice(`Installation set to ${status}.`);
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Could not update installation');
    }
  };

  const updateTicket = async (id: string, body: Record<string, any>) => {
    try {
      await apiService.updateCompanionTicket(id, body, scope);
      await loadAll();
      setNotice('Ticket updated.');
    } catch (e: any) {
      setError(e?.message || 'Could not update ticket');
    }
  };

  const openAppDeployment = (row: any) => {
    // Seed App Deployment with a useful starting point when IT is fulfilling a
    // software request from Companion.
    try {
      const hint = String(row.subject || '')
        .replace(/required|please|install|installation|need|for/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      sessionStorage.setItem(
        'uem_app_deploy_hint',
        JSON.stringify({
          search: hint || '',
          platform: String(row.device?.os || 'windows').toLowerCase(),
          device_id: row.device_id || row.device?.device_id || null,
          device_name: row.device?.name || null,
          ticket_no: row.ticket_no,
        })
      );
    } catch {
      /* ignore */
    }
    if (onNavigate) {
      onNavigate('app-deployment');
      return;
    }
    setNotice('Open Apps & Deployment from the sidebar to fulfil this request.');
  };

  const sendNotification = async () => {
    if (!notifDraft.title.trim() || !notifDraft.message.trim()) {
      setError('Notification needs a title and a message.');
      return;
    }
    try {
      await apiService.createCompanionNotification(
        {
          title: notifDraft.title.trim(),
          message: notifDraft.message.trim(),
          severity: notifDraft.severity || undefined,
          category: notifDraft.category || undefined,
          audience: 'all',
        },
        scope
      );
      setNotifDraft({ title: '', message: '', severity: '', category: '' });
      setNotice('Portal notification sent. This does not appear on the floating Windows companion.');
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Could not send notification');
    }
  };

  const notifCfg = settings?.config?.notifications || {};
  const severities: string[] = (notifCfg.severities as string[]) || [];
  const categories: string[] = (notifCfg.categories as string[]) || [];

  return (
    <PageLayout
      title="Buntu Companion"
      subtitle={
        isPlatform
          ? 'Companion adoption, support and configuration across all tenants.'
          : 'Desktop character (Windows speech bubbles) and companion portal in one place.'
      }
      icon={<MonitorSmartphone size={20} />}
      eyebrow={isPlatform ? 'Platform' : 'Tenant'}
      action={
        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      }
    >
      {isPlatform && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tenant</label>
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm min-w-[240px]"
              >
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name} ({t.tenant_code})
                  </option>
                ))}
              </select>
            </div>
            {tenantRow && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={tenantRow.enabled ? 'green' : 'slate'}>
                  {tenantRow.enabled ? 'Companion enabled' : 'Companion off'}
                </Badge>
                <Badge tone={tenantRow.platform_allowed ? 'blue' : 'red'}>
                  {tenantRow.platform_allowed ? 'Allowed by platform' : 'Withheld by platform'}
                </Badge>
                <button
                  type="button"
                  onClick={() => toggleTenantAllowed(!tenantRow.platform_allowed)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                >
                  {tenantRow.platform_allowed ? 'Withhold companion' : 'Allow companion'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500">
            Dismiss
          </button>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle size={16} className="mt-0.5" />
          <span className="flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-emerald-600">
            Dismiss
          </button>
        </div>
      )}

      {!isPlatform && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSurface('desktop')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              surface === 'desktop'
                ? 'bg-blue-600 text-white'
                : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            Desktop character
          </button>
          <button
            type="button"
            onClick={() => setSurface('portal')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              surface === 'portal'
                ? 'bg-blue-600 text-white'
                : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            Companion portal
          </button>
        </div>
      )}

      {!isPlatform && surface === 'desktop' && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="mb-4 text-sm text-slate-600">
            These messages reach the floating Windows companion. Portal notifications (Companion portal
            mode) stay in the web companion inbox only.
          </p>
          <BuntuCompanionSettings />
        </div>
      )}

      {(isPlatform || surface === 'portal') && (
      <HubPanel withTabs padded={false}>
        <HubTabs
          items={TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
          value={activeTab}
          onChange={setActiveTab}
        />
        <div className="hub-panel__inner">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  label="Total installations"
                  value={overview?.installations_total ?? 0}
                  icon={<Laptop size={16} className="text-slate-400" />}
                />
                <StatCard
                  label="Active companions"
                  value={overview?.installations_active ?? 0}
                  hint="Seen in the last 7 days"
                  icon={<CheckCircle size={16} className="text-emerald-500" />}
                />
                <StatCard
                  label="Outdated companions"
                  value={overview?.installations_outdated ?? 0}
                  hint={overview?.min_supported_version ? `Below ${overview.min_supported_version}` : 'No minimum version set'}
                  icon={<AlertCircle size={16} className="text-amber-500" />}
                />
                <StatCard
                  label="Devices without companion"
                  value={overview?.devices_without_companion ?? 0}
                  hint={`${overview?.adoption_percent ?? 0}% adoption`}
                  icon={<TrendingUp size={16} className="text-blue-500" />}
                />
                <StatCard
                  label="Tickets raised"
                  value={overview?.tickets_total ?? 0}
                  hint={`${overview?.tickets_open ?? 0} still open`}
                  icon={<LifeBuoy size={16} className="text-slate-400" />}
                />
                <StatCard
                  label="Consent pending"
                  value={overview?.consent_pending ?? 0}
                  icon={<ShieldCheck size={16} className="text-slate-400" />}
                />
                <StatCard
                  label="Managed devices"
                  value={overview?.devices_total ?? 0}
                  hint={(overview?.supported_os || []).join(', ') || '—'}
                  icon={<MonitorSmartphone size={16} className="text-slate-400" />}
                />
                <StatCard
                  label="Companion status"
                  value={overview?.enabled ? 'Enabled' : 'Disabled'}
                  icon={<Sparkles size={16} className="text-slate-400" />}
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-slate-800">OS-wise adoption</h4>
                  {(overview?.os_adoption || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No installations yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {overview.os_adoption.map((row: any) => (
                        <li key={row.os_type} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{row.os_type}</span>
                          <span className="font-medium text-slate-800">{row.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-slate-800">Version distribution</h4>
                  {(overview?.version_distribution || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No installations yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {overview.version_distribution.map((row: any) => (
                        <li key={row.version} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{row.version}</span>
                          <span className="font-medium text-slate-800">{row.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {isPlatform && (
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-slate-800">All tenants</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="py-2 pr-4">Tenant</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Installs</th>
                          <th className="py-2 pr-4">Open tickets</th>
                          <th className="py-2 pr-4">Consent pending</th>
                          <th className="py-2 pr-4">Locked sections</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenants.map((t) => (
                          <tr key={t.tenant_id} className="border-t border-slate-100">
                            <td className="py-2 pr-4">
                              <button
                                type="button"
                                onClick={() => setSelectedTenant(t.tenant_id)}
                                className="text-blue-600 hover:underline"
                              >
                                {t.tenant_name}
                              </button>
                            </td>
                            <td className="py-2 pr-4">
                              {t.platform_allowed ? (
                                <Badge tone={t.enabled ? 'green' : 'slate'}>
                                  {t.enabled ? 'Enabled' : 'Off'}
                                </Badge>
                              ) : (
                                <Badge tone="red">Withheld</Badge>
                              )}
                            </td>
                            <td className="py-2 pr-4">{t.installations}</td>
                            <td className="py-2 pr-4">{t.tickets_open}</td>
                            <td className="py-2 pr-4">{t.consent_pending}</td>
                            <td className="py-2 pr-4 text-slate-500">
                              {(t.locked_sections || []).join(', ') || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'installations' && (
            <div className="overflow-x-auto">
              {installations.length === 0 ? (
                <p className="py-6 text-sm text-slate-500">No companion installations recorded yet.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Companion</th>
                      <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4">Device</th>
                      <th className="py-2 pr-4">OS</th>
                      <th className="py-2 pr-4">Version</th>
                      <th className="py-2 pr-4">Mode</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Last seen</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installations.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100">
                        <td className="py-2 pr-4 font-mono text-xs text-slate-600">{row.companion_id}</td>
                        <td className="py-2 pr-4">{row.username || '—'}</td>
                        <td className="py-2 pr-4">{row.device_name || '—'}</td>
                        <td className="py-2 pr-4">{row.os_type}</td>
                        <td className="py-2 pr-4">{row.app_version || '—'}</td>
                        <td className="py-2 pr-4">{row.install_mode}</td>
                        <td className="py-2 pr-4">
                          <Badge tone={row.status === 'active' ? 'green' : row.status === 'blocked' ? 'red' : 'slate'}>
                            {row.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-slate-500">{fmtDate(row.last_seen_at)}</td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => updateInstallation(row.id, row.status === 'active' ? 'blocked' : 'active')}
                              className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                            >
                              {row.status === 'active' ? 'Block' : 'Activate'}
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await apiService.revokeCompanionSessions(row.id, scope);
                                  setNotice(res?.message || 'Sessions revoked.');
                                } catch (e: any) {
                                  setError(e?.message || 'Could not revoke sessions');
                                }
                              }}
                              className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                            >
                              Revoke sessions
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'adoption' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">OS</th>
                    <th className="py-2 pr-4">Managed devices</th>
                    <th className="py-2 pr-4">With companion</th>
                    <th className="py-2 pr-4">Without companion</th>
                    <th className="py-2 pr-4">Adoption</th>
                  </tr>
                </thead>
                <tbody>
                  {adoption.map((row) => (
                    <tr key={row.os_type} className="border-t border-slate-100">
                      <td className="py-2 pr-4">{row.os_type}</td>
                      <td className="py-2 pr-4">{row.devices ?? '—'}</td>
                      <td className="py-2 pr-4">{row.with_companion}</td>
                      <td className="py-2 pr-4">{row.without_companion ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {row.adoption_percent == null ? '—' : `${row.adoption_percent}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-slate-500">
                Mobile companions are not part of this release, so only the configured desktop and web
                surfaces appear here.
              </p>
            </div>
          )}

          {activeTab === 'tickets' && (
            <div className="space-y-3">
              {tickets.length === 0 ? (
                <p className="py-6 text-sm text-slate-500">No support tickets raised from the companion yet.</p>
              ) : (
                tickets.map((row) => {
                  const open = selectedTicketId === row.id;
                  const device = row.device || {};
                  const requester = row.requester || {};
                  const requesterLabel =
                    requester.full_name || requester.username || requester.email || 'Signed-in via device';
                  const draft =
                    resolutionDrafts[row.id] ??
                    (row.resolution_note || '');
                  return (
                    <div key={row.id} className="rounded-lg border border-slate-200 bg-white">
                      <button
                        type="button"
                        onClick={() => setSelectedTicketId(open ? null : row.id)}
                        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs text-slate-500">{row.ticket_no}</span>
                            <Badge
                              tone={
                                row.status === 'open'
                                  ? 'amber'
                                  : row.status === 'in_progress'
                                  ? 'blue'
                                  : 'green'
                              }
                            >
                              {row.status}
                            </Badge>
                            <Badge tone={row.priority === 'urgent' || row.priority === 'critical' ? 'red' : 'slate'}>
                              {row.priority}
                            </Badge>
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-slate-600">
                              {row.category}
                            </span>
                          </div>
                          <p className="mt-1 font-medium text-slate-800">{row.subject}</p>
                          <p className="mt-0.5 truncate text-xs text-slate-500">
                            {device.name || 'Unknown device'}
                            {device.os ? ` · ${device.os}${device.os_version ? ` ${device.os_version}` : ''}` : ''}
                            {' · '}
                            {fmtDate(row.created_at)}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs text-slate-400">{open ? 'Hide' : 'Details'}</span>
                      </button>

                      {open && (
                        <div className="space-y-4 border-t border-slate-100 px-4 py-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Request
                              </h4>
                              <p className="whitespace-pre-wrap text-sm text-slate-800">
                                {row.description || row.subject}
                              </p>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Device
                                </h4>
                                <dl className="space-y-1 text-sm">
                                  {[
                                    ['Name', device.name || '—'],
                                    ['OS', `${device.os || '—'}${device.os_version ? ` · ${device.os_version}` : ''}`],
                                    ['Compliance', device.compliance_status || '—'],
                                    ['Agent', device.agent_version || '—'],
                                    ['Last sync', fmtDate(device.last_sync)],
                                    ['Pending patches', String(row.patches?.pending_count ?? '—')],
                                    ['Device ID', device.device_id || row.device_id || '—'],
                                  ].map(([label, value]) => (
                                    <div key={String(label)} className="flex justify-between gap-3">
                                      <dt className="text-slate-500">{label}</dt>
                                      <dd className="max-w-[60%] truncate text-right text-slate-800">{value}</dd>
                                    </div>
                                  ))}
                                </dl>
                              </div>
                              <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Raised by
                                </h4>
                                <p className="text-sm text-slate-800">{requesterLabel}</p>
                                {requester.email && (
                                  <p className="text-xs text-slate-500">{requester.email}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
                              <select
                                value={row.status}
                                onChange={(e) => updateTicket(row.id, { status: e.target.value })}
                                className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                              >
                                {['open', 'in_progress', 'resolved', 'closed'].map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="min-w-[220px] flex-1">
                              <label className="mb-1 block text-xs font-medium text-slate-500">
                                Resolution note
                              </label>
                              <input
                                type="text"
                                value={draft}
                                placeholder="e.g. Firefox 128 deployed via App Deployment"
                                onChange={(e) =>
                                  setResolutionDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                                }
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                updateTicket(row.id, {
                                  resolution_note: draft.trim() || null,
                                  status: row.status === 'open' ? 'in_progress' : row.status,
                                })
                              }
                              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Save note
                            </button>
                            {String(row.category || '').toLowerCase() === 'software' && (
                              <button
                                type="button"
                                onClick={() => openAppDeployment(row)}
                                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                              >
                                <Package size={14} /> Open App Deployment
                              </button>
                            )}
                          </div>
                          {device.reboot_required && (
                            <p className="text-xs text-amber-700">
                              This device currently reports that a reboot is required.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-5">
              <div className="rounded-lg border border-slate-200 p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Bell size={16} className="text-blue-600" /> Send a portal notification
                </h4>
                <p className="mb-3 text-xs text-slate-500">
                  Appears in the web companion inbox only. For the Windows speech bubble, use Desktop
                  character → Messages.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">Title</label>
                    <input
                      type="text"
                      value={notifDraft.title}
                      onChange={(e) => setNotifDraft({ ...notifDraft, title: e.target.value })}
                      className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">Severity</label>
                      <select
                        value={notifDraft.severity}
                        onChange={(e) => setNotifDraft({ ...notifDraft, severity: e.target.value })}
                        className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
                      >
                        <option value="">Default</option>
                        {severities.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">Category</label>
                      <select
                        value={notifDraft.category}
                        onChange={(e) => setNotifDraft({ ...notifDraft, category: e.target.value })}
                        className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
                      >
                        <option value="">Default</option>
                        {categories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Message</label>
                    <textarea
                      rows={3}
                      value={notifDraft.message}
                      onChange={(e) => setNotifDraft({ ...notifDraft, message: e.target.value })}
                      className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={sendNotification}
                  className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <Send size={14} /> Send to everyone
                </button>
                <p className="mt-2 text-xs text-slate-500">
                  Severities and categories come from the tenant configuration, so the choices always match policy.
                </p>
              </div>

              <div className="overflow-x-auto">
                {notifications.length === 0 ? (
                  <p className="py-4 text-sm text-slate-500">Nothing sent yet.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-4">Title</th>
                        <th className="py-2 pr-4">Severity</th>
                        <th className="py-2 pr-4">Category</th>
                        <th className="py-2 pr-4">Target</th>
                        <th className="py-2 pr-4">Read</th>
                        <th className="py-2 pr-4">Sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="py-2 pr-4">
                            <p className="font-medium text-slate-800">{row.title}</p>
                            <p className="text-xs text-slate-500">{row.message}</p>
                          </td>
                          <td className="py-2 pr-4">{row.severity}</td>
                          <td className="py-2 pr-4">{row.category}</td>
                          <td className="py-2 pr-4 text-slate-500">
                            {row.user_id ? 'user' : row.device_id ? 'device' : 'organisation'}
                          </td>
                          <td className="py-2 pr-4">{row.read_at ? 'yes' : 'no'}</td>
                          <td className="py-2 pr-4 text-slate-500">{fmtDate(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {activeTab === 'consents' && (
            <div className="overflow-x-auto">
              {consents.length === 0 ? (
                <p className="py-6 text-sm text-slate-500">No remote support consent requests recorded.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Request</th>
                      <th className="py-2 pr-4">Device</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Requested</th>
                      <th className="py-2 pr-4">Responded</th>
                      <th className="py-2 pr-4">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consents.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100">
                        <td className="py-2 pr-4">{row.request_type}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-slate-600">{row.device_id || '—'}</td>
                        <td className="py-2 pr-4">
                          <Badge
                            tone={
                              row.status === 'approved'
                                ? 'green'
                                : row.status === 'pending'
                                ? 'amber'
                                : row.status === 'denied'
                                ? 'red'
                                : 'slate'
                            }
                          >
                            {row.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-slate-500">{fmtDate(row.requested_at)}</td>
                        <td className="py-2 pr-4 text-slate-500">{fmtDate(row.responded_at)}</td>
                        <td className="py-2 pr-4 text-slate-500">{fmtDate(row.expires_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-3 text-xs text-slate-500">
                Every request and response is written to the audit log; consent expires server-side.
              </p>
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <h4 className="mb-2 text-sm font-semibold text-slate-800">Version policy</h4>
                <dl className="grid gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-slate-500">Minimum supported</dt>
                    <dd className="text-slate-800">{versions?.policy?.min_supported_version || 'not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Latest published</dt>
                    <dd className="text-slate-800">{versions?.policy?.latest_version || 'not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Outdated grace days</dt>
                    <dd className="text-slate-800">{versions?.policy?.outdated_grace_days ?? '—'}</dd>
                  </div>
                </dl>
                <p className="mt-2 text-xs text-slate-500">
                  Edit these in the Settings tab; download links per OS live there too.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">OS</th>
                      <th className="py-2 pr-4">Version</th>
                      <th className="py-2 pr-4">Installs</th>
                      <th className="py-2 pr-4">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(versions?.items || []).map((row: any) => (
                      <tr key={`${row.os_type}-${row.app_version}`} className="border-t border-slate-100">
                        <td className="py-2 pr-4">{row.os_type}</td>
                        <td className="py-2 pr-4">{row.app_version}</td>
                        <td className="py-2 pr-4">{row.count}</td>
                        <td className="py-2 pr-4">
                          <Badge tone={row.outdated ? 'amber' : 'green'}>
                            {row.outdated ? 'outdated' : 'supported'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Sparkles size={16} className="text-blue-600" /> Ask Buntu
                </h4>
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-slate-500">Enabled</dt>
                    <dd className="text-slate-800">{settings?.config?.ai?.enabled ? 'Yes' : 'No'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Provider</dt>
                    <dd className="text-slate-800">{settings?.config?.ai?.provider || 'none'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Welcome text</dt>
                    <dd className="text-slate-800">{settings?.config?.ai?.welcome_text || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Rate limit (per hour)</dt>
                    <dd className="text-slate-800">{settings?.config?.ai?.rate_limit_per_hour ?? '—'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">Placeholder reply shown while disabled</dt>
                    <dd className="text-slate-800">{settings?.config?.ai?.placeholder_reply || '—'}</dd>
                  </div>
                </dl>
                <p className="mt-3 text-xs text-slate-500">
                  The assistant backend is not wired yet. Until it is, the companion returns the placeholder
                  reply above. All of these values are editable in the Settings tab.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-8">
              <CompanionSettingsForm
                payload={settings}
                loading={loading}
                saving={saving}
                canEditLocked={isPlatform}
                onSave={saveSettings}
                headerExtra={
                  isPlatform && settings ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Lock size={14} /> Sections locked for the tenant admin
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {(settings.config_sections || []).map((section) => {
                          const locked = (settings.locked_sections || []).includes(section);
                          return (
                            <label key={section} className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={locked}
                                onChange={() => toggleSectionLock(section)}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600"
                              />
                              {section}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null
                }
              />

              {isPlatform && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="mb-1 text-base font-semibold text-slate-800">Platform defaults</h3>
                  <p className="mb-4 text-sm text-slate-600">
                    Baseline applied to tenants that have not been configured yet.
                  </p>
                  <CompanionSettingsForm
                    payload={platformDefaults}
                    loading={loading}
                    saving={saving}
                    canEditLocked
                    onSave={savePlatformDefaults}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Web companion preview (portal inbox and device status). This is not the floating desktop
                character — use Desktop character → Preview for that.
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50">
                <CompanionUserApp hubToken={hubToken} />
              </div>
            </div>
          )}
        </div>
      </HubPanel>
      )}
    </PageLayout>
  );
};

export default BuntuCompanionHub;
